import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Skeleton,
  FormGroup, FormControlLabel, Checkbox, Grid, Alert,
  Dialog, DialogTitle, DialogContent, Button,
  Divider, IconButton, Tooltip, Paper, Stack,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Info, Close, TrendingUp, TrendingDown, TrendingFlat, Warning,
  Analytics as AnalyticsIcon, Speed as SpeedIcon,
} from '@mui/icons-material';
import {
  ComposedChart, Line, Area, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer, ReferenceLine, Scatter, Cell,
} from 'recharts';

import { apiFetch } from '../../utils/apiFetch';

interface AnalysisData {
  market: string;
  current_price: number;
  ticker: { high_24h: number; low_24h: number; volume_24h: number; change_24h_pct: number };
  candles: Array<{ timestamp: number; open: number; high: number; low: number; close: number; volume: number }>;
  indicators: Record<string, (number | null)[]>;
  patterns: Array<{
    type: string; name: string; signal: string; severity: string;
    description: string; explanation: string; action: string;
  }>;
}

// All available overlay indicators with metadata
const INDICATOR_INFO: Record<string, { name: string; color: string; description: string }> = {
  sma7: { name: 'SMA 7', color: '#FF9800', description: 'Kurzfristiger gleitender Durchschnitt (7 Perioden).' },
  sma20: { name: 'SMA 20', color: '#2196F3', description: 'Mittelfristiger Durchschnitt — oft als Unterstützung/Widerstand.' },
  sma50: { name: 'SMA 50', color: '#9C27B0', description: 'Langfristiger Durchschnitt — institutioneller Trend.' },
  ema12: { name: 'EMA 12', color: '#00BCD4', description: 'Schneller exponentieller Durchschnitt, reagiert schnell auf Preisänderungen.' },
  ema20: { name: 'EMA 20', color: '#8BC34A', description: 'EMA 20 — mittelfristiger Trend, wichtig für RSI-Reversal-Strategien.' },
  ema26: { name: 'EMA 26', color: '#795548', description: 'Langsamer EMA — bildet mit EMA 12 die Basis für MACD.' },
  bb_upper: { name: 'Bollinger Oben', color: '#E91E63', description: 'Oberes Band (SMA20 + 2σ) — statistisch "teuer".' },
  bb_middle: { name: 'Bollinger Mitte', color: '#E91E63', description: 'Mittleres Bollinger Band (= SMA 20).' },
  bb_lower: { name: 'Bollinger Unten', color: '#E91E63', description: 'Unteres Band (SMA20 - 2σ) — statistisch "günstig".' },
  vwap: { name: 'VWAP', color: '#FF5722', description: 'Volumengewichteter Durchschnittspreis.' },
};

// Which indicators each strategy type needs (pre-selected)
const STRATEGY_INDICATORS: Record<string, { overlays: string[]; subCharts: string[]; description: string }> = {
  marktmeister: {
    overlays: ['ema12', 'ema20', 'sma7', 'sma20', 'bb_upper', 'bb_middle', 'bb_lower', 'vwap'],
    subCharts: ['rsi', 'macd', 'stochastic', 'roc', 'volume'],
    description: 'MarktMeister erkennt Marktphasen automatisch und passt Strategie und Indikatoren adaptiv an.',
  },
};

interface StrategyTrade {
  id: number;
  timestamp: number;
  side: 'buy' | 'sell';
  amount: number;
  price: number;
  total: number;
  fee: number;
  reason: string;
  market: string;
}

interface StrategyAnalysisProps {
  market: string;
  strategyType?: string;
  strategyName?: string;
  strategyId?: number;
  open: boolean;
  onClose: () => void;
}

const StrategyAnalysis: React.FC<StrategyAnalysisProps> = ({ market, strategyType, strategyName, strategyId, open, onClose }) => {
  const [data, setData] = useState<AnalysisData | null>(null);
  const [trades, setTrades] = useState<StrategyTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPattern, setExpandedPattern] = useState<string | null>(null);
  const [interval, setInterval] = useState('1h');

  // Determine which indicators to show based on strategy type
  const strategyConfig = strategyType ? STRATEGY_INDICATORS[strategyType] : null;

  const [selectedIndicators, setSelectedIndicators] = useState<Record<string, boolean>>({});

  // Reset indicator selection when strategy type changes
  useEffect(() => {
    if (strategyConfig) {
      const preSelected: Record<string, boolean> = {};
      strategyConfig.overlays.forEach(k => { preSelected[k] = true; });
      setSelectedIndicators(preSelected);
    } else {
      setSelectedIndicators({ sma20: true, bb_upper: true, bb_lower: true });
    }
  }, [strategyType]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);

    // Load analysis data + strategy trades in parallel
    const promises: Promise<any>[] = [
      apiFetch(`/paper/analysis/${market}?interval=${interval}`)
        .then((r) => { if (!r.ok) throw new Error('Analyse fehlgeschlagen'); return r.json(); })
        .then(setData),
    ];

    if (strategyId) {
      promises.push(
        apiFetch(`/paper/strategy-trades/${strategyId}`)
          .then((r) => r.ok ? r.json() : [])
          .then(setTrades)
          .catch(() => setTrades([]))
      );
    }

    Promise.all(promises)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [market, strategyId, open, interval]);

  const toggleIndicator = (key: string) => {
    setSelectedIndicators((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Match trades to candle timestamps
  const tradesByCandle = useMemo(() => {
    if (!data || trades.length === 0) return new Map<number, StrategyTrade[]>();
    const map = new Map<number, StrategyTrade[]>();
    for (const t of trades) {
      // Find closest candle
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < data.candles.length; i++) {
        const dist = Math.abs(data.candles[i].timestamp - t.timestamp);
        if (dist < bestDist) { bestDist = dist; bestIdx = i; }
      }
      if (!map.has(bestIdx)) map.set(bestIdx, []);
      map.get(bestIdx)!.push(t);
    }
    return map;
  }, [data, trades]);

  // Build chart data
  const chartData = useMemo(() => {
    if (!data) return [];
    return data.candles.map((c, i) => {
      const point: any = {
        time: new Date(c.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
        price: c.close,
        open: c.open,
        high: c.high,
        low: c.low,
        volume: c.volume,
      };
      // Add trade markers
      const candleTrades = tradesByCandle.get(i);
      if (candleTrades) {
        const buy = candleTrades.find(t => t.side === 'buy');
        const sell = candleTrades.find(t => t.side === 'sell');
        if (buy) point.buyMarker = buy.price;
        if (sell) point.sellMarker = sell.price;
        point.tradeInfo = candleTrades;
      }
      Object.keys(INDICATOR_INFO).forEach((key) => {
        if (selectedIndicators[key] && data.indicators[key]) {
          point[key] = data.indicators[key][i];
        }
      });
      // Sub-chart data
      if (data.indicators.rsi) point.rsi = data.indicators.rsi[i];
      if (data.indicators.macd_line) point.macd = data.indicators.macd_line[i];
      if (data.indicators.macd_signal) point.macd_signal = data.indicators.macd_signal[i];
      if (data.indicators.macd_histogram) point.macd_hist = data.indicators.macd_histogram[i];
      if (data.indicators.stochastic_k) point.stoch_k = data.indicators.stochastic_k[i];
      if (data.indicators.roc) point.roc = data.indicators.roc[i];
      if (data.indicators.volume_sma) point.vol_sma = data.indicators.volume_sma[i];
      // Bollinger Band Width
      if (data.indicators.bb_upper && data.indicators.bb_lower && data.indicators.bb_middle) {
        const bbu = data.indicators.bb_upper[i];
        const bbl = data.indicators.bb_lower[i];
        const bbm = data.indicators.bb_middle[i];
        if (bbu != null && bbl != null && bbm != null && bbm > 0) {
          point.bb_width = +((bbu - bbl) / bbm * 100).toFixed(2);
        }
      }
      return point;
    });
  }, [data, selectedIndicators]);

  const subChartsToShow = strategyConfig?.subCharts ?? ['rsi', 'macd'];

  const signalIcon = (signal: string) => {
    if (signal === 'bullish') return <TrendingUp sx={{ color: 'success.main' }} />;
    if (signal === 'bearish') return <TrendingDown sx={{ color: 'error.main' }} />;
    return <TrendingFlat sx={{ color: 'text.secondary' }} />;
  };

  const severityColor = (severity: string) => {
    if (severity === 'high') return 'error';
    if (severity === 'medium') return 'warning';
    return 'default';
  };

  if (!open) return null;

  const xInterval = Math.max(1, Math.floor(chartData.length / 10));
  const xIntervalSub = Math.max(1, Math.floor(chartData.length / 6));

  const tooltipStyle = { backgroundColor: 'rgba(0,0,0,0.85)', border: 'none', borderRadius: 8, fontSize: 12 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '92vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AnalyticsIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {strategyName ? `${strategyName} — ${market}` : `Analyse: ${market}`}
              </Typography>
              {strategyConfig && (
                <Typography variant="caption" color="text.secondary">{strategyConfig.description}</Typography>
              )}
            </Box>
            {data && (
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                <Chip label={`€${data.current_price.toLocaleString('de-DE', { minimumFractionDigits: 2 })}`} />
                <Chip
                  label={`${data.ticker.change_24h_pct >= 0 ? '+' : ''}${data.ticker.change_24h_pct.toFixed(2)}%`}
                  color={data.ticker.change_24h_pct >= 0 ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        {loading && (
          <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 2 }} />
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {data && !loading && (
          <Box sx={{ p: 2 }}>
            {/* Strategy-specific info banner */}
            {strategyConfig && (
              <Alert severity="info" icon={<SpeedIcon />} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Strategie-Indikatoren automatisch ausgewählt
                </Typography>
                <Typography variant="body2">
                  Die für diese Strategie relevanten Indikatoren sind vorausgewählt. Du kannst weitere aktivieren/deaktivieren.
                </Typography>
              </Alert>
            )}

            {/* Zeitraum-Auswahl */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                Zeitraum:
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={interval}
                onChange={(_, v) => { if (v) setInterval(v); }}
                size="small"
              >
                <ToggleButton value="1m" sx={{ px: 1.5, textTransform: 'none' }}>1m</ToggleButton>
                <ToggleButton value="5m" sx={{ px: 1.5, textTransform: 'none' }}>5m</ToggleButton>
                <ToggleButton value="15m" sx={{ px: 1.5, textTransform: 'none' }}>15m</ToggleButton>
                <ToggleButton value="1h" sx={{ px: 1.5, textTransform: 'none' }}>1h</ToggleButton>
                <ToggleButton value="4h" sx={{ px: 1.5, textTransform: 'none' }}>4h</ToggleButton>
                <ToggleButton value="1d" sx={{ px: 1.5, textTransform: 'none' }}>1d</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Indicator toggles */}
            <Card sx={{ borderRadius: 2, mb: 2 }}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mr: 2 }}>Overlay-Indikatoren:</Typography>
                  <FormGroup row>
                    {Object.entries(INDICATOR_INFO).map(([key, info]) => {
                      const isStrategyIndicator = strategyConfig?.overlays.includes(key);
                      return (
                        <Tooltip key={key} title={info.description} arrow placement="top">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!selectedIndicators[key]}
                                onChange={() => toggleIndicator(key)}
                                size="small"
                                sx={{ color: info.color, '&.Mui-checked': { color: info.color } }}
                              />
                            }
                            label={
                              <Typography variant="caption" sx={{ fontWeight: isStrategyIndicator ? 700 : 400 }}>
                                {info.name}{isStrategyIndicator ? ' ★' : ''}
                              </Typography>
                            }
                            sx={{ mr: 1 }}
                          />
                        </Tooltip>
                      );
                    })}
                  </FormGroup>
                </Box>
              </CardContent>
            </Card>

            {/* ═══ Main Price Chart ═══ */}
            <Card sx={{ borderRadius: 2, mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Preis-Chart (1h Kerzen) — {market}
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={xInterval} />
                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} />
                    <ReTooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number, name: string) => {
                        const label = INDICATOR_INFO[name]?.name || (name === 'price' ? 'Preis' : name);
                        return [typeof value === 'number' ? `€${value.toFixed(2)}` : value, label];
                      }}
                    />
                    <Line type="monotone" dataKey="price" stroke="#4CAF50" strokeWidth={2} dot={false} name="price" />
                    {Object.entries(INDICATOR_INFO).map(([key, info]) =>
                      selectedIndicators[key] ? (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={info.color}
                          strokeWidth={key.startsWith('bb_') ? 1 : 1.5}
                          dot={false}
                          strokeDasharray={key.startsWith('bb_') ? '4 4' : undefined}
                          name={key}
                        />
                      ) : null
                    )}
                    {/* Buy markers */}
                    <Scatter dataKey="buyMarker" name="Kauf" shape="triangle" fill="#4CAF50" >
                      {chartData.map((entry, idx) => (
                        <Cell key={idx} fill="#4CAF50" stroke="#fff" strokeWidth={1} />
                      ))}
                    </Scatter>
                    {/* Sell markers */}
                    <Scatter dataKey="sellMarker" name="Verkauf" shape="diamond" fill="#EF5350">
                      {chartData.map((entry, idx) => (
                        <Cell key={idx} fill="#EF5350" stroke="#fff" strokeWidth={1} />
                      ))}
                    </Scatter>
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* ═══ Sub-Charts — strategy-specific ═══ */}
            <Grid container spacing={2} sx={{ mb: 2 }}>

              {/* RSI */}
              {subChartsToShow.includes('rsi') && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>RSI (14)</Typography>
                        {strategyType === 'rsi_reversal' && <Chip label="Kern-Indikator" size="small" color="primary" />}
                        <Tooltip title="RSI misst ob überkauft (>70) oder überverkauft (<30). RSI Reversal kauft bei RSI < 25." arrow>
                          <Info sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={160}>
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                          <XAxis dataKey="time" tick={{ fontSize: 9 }} interval={xIntervalSub} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                          <ReTooltip contentStyle={tooltipStyle} />
                          <ReferenceLine y={70} stroke="#EF5350" strokeDasharray="3 3" label={{ value: '70 Überkauft', fontSize: 9, fill: '#EF5350' }} />
                          <ReferenceLine y={30} stroke="#4CAF50" strokeDasharray="3 3" label={{ value: '30 Überverkauft', fontSize: 9, fill: '#4CAF50' }} />
                          {strategyType === 'rsi_reversal' && (
                            <ReferenceLine y={25} stroke="#FF5722" strokeWidth={2} strokeDasharray="6 3" label={{ value: '25 Kaufzone', fontSize: 9, fill: '#FF5722' }} />
                          )}
                          {strategyType === 'trailing_trend' && (
                            <>
                              <ReferenceLine y={40} stroke="#2196F3" strokeDasharray="2 4" label={{ value: '40', fontSize: 8, fill: '#2196F3' }} />
                              <ReferenceLine y={80} stroke="#FF5722" strokeDasharray="2 4" label={{ value: '80 Teilverkauf', fontSize: 8, fill: '#FF5722' }} />
                            </>
                          )}
                          <Area type="monotone" dataKey="rsi" stroke="#FF9800" fill="rgba(255,152,0,0.1)" strokeWidth={1.5} dot={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* MACD */}
              {subChartsToShow.includes('macd') && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>MACD</Typography>
                        {strategyType === 'trailing_trend' && <Chip label="Kern-Indikator" size="small" color="primary" />}
                        <Tooltip title="MACD-Histogramm positiv = Aufwärtsmomentum. Trend Reiter braucht positives Histogramm als Bestätigung." arrow>
                          <Info sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={160}>
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                          <XAxis dataKey="time" tick={{ fontSize: 9 }} interval={xIntervalSub} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <ReTooltip contentStyle={tooltipStyle} />
                          <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" />
                          <Bar dataKey="macd_hist" name="Histogram">
                            {chartData.map((entry, idx) => (
                              <rect key={idx} fill={entry.macd_hist >= 0 ? 'rgba(76,175,80,0.5)' : 'rgba(244,67,54,0.5)'} />
                            ))}
                          </Bar>
                          <Line type="monotone" dataKey="macd" stroke="#2196F3" strokeWidth={1.5} dot={false} name="MACD" />
                          <Line type="monotone" dataKey="macd_signal" stroke="#FF9800" strokeWidth={1.5} dot={false} name="Signal" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Stochastic */}
              {subChartsToShow.includes('stochastic') && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>Stochastic %K (14)</Typography>
                        <Chip label="Kern-Indikator" size="small" color="primary" />
                        <Tooltip title="Stochastic misst Position des Preises relativ zum High/Low-Range. Scalping kauft wenn %K > %D und < 80." arrow>
                          <Info sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={160}>
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                          <XAxis dataKey="time" tick={{ fontSize: 9 }} interval={xIntervalSub} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                          <ReTooltip contentStyle={tooltipStyle} />
                          <ReferenceLine y={80} stroke="#EF5350" strokeDasharray="3 3" label={{ value: '80 Überkauft', fontSize: 9, fill: '#EF5350' }} />
                          <ReferenceLine y={20} stroke="#4CAF50" strokeDasharray="3 3" label={{ value: '20 Überverkauft', fontSize: 9, fill: '#4CAF50' }} />
                          <Area type="monotone" dataKey="stoch_k" stroke="#00BCD4" fill="rgba(0,188,212,0.1)" strokeWidth={1.5} dot={false} name="%K" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* ROC (Rate of Change) */}
              {subChartsToShow.includes('roc') && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>ROC — Rate of Change (12)</Typography>
                        <Chip label="Momentum" size="small" color="warning" />
                        <Tooltip title="ROC zeigt die prozentuale Preisänderung über 12 Perioden. Positiv = Aufwärtsbewegung, Negativ = Abwärtsbewegung." arrow>
                          <Info sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={160}>
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                          <XAxis dataKey="time" tick={{ fontSize: 9 }} interval={xIntervalSub} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <ReTooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v?.toFixed(2)}%`, 'ROC']} />
                          <ReferenceLine y={0} stroke="rgba(255,255,255,0.3)" />
                          <Area type="monotone" dataKey="roc" stroke="#FF5722" fill="rgba(255,87,34,0.08)" strokeWidth={1.5} dot={false} name="ROC" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Volume with SMA */}
              {subChartsToShow.includes('volume') && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>Volume + SMA(20)</Typography>
                        <Chip label="Kern-Indikator" size="small" color="primary" />
                        <Tooltip title="Breakout-Strategie braucht Volume ≥ 1.5× des Durchschnitts als Bestätigung für echte Ausbrüche." arrow>
                          <Info sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={160}>
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                          <XAxis dataKey="time" tick={{ fontSize: 9 }} interval={xIntervalSub} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <ReTooltip contentStyle={tooltipStyle} />
                          <Bar dataKey="volume" fill="rgba(33,150,243,0.3)" name="Volume" />
                          <Line type="monotone" dataKey="vol_sma" stroke="#FF9800" strokeWidth={1.5} dot={false} name="Volume SMA(20)" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Bollinger Band Width (Squeeze detection) */}
              {subChartsToShow.includes('bbwidth') && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>Bollinger Bandbreite (%)</Typography>
                        <Chip label="Squeeze-Detektor" size="small" color="primary" />
                        <Tooltip title="Enge Bänder (niedrige Bandbreite) = Squeeze. Ein Breakout nach einem Squeeze führt oft zu großen Moves." arrow>
                          <Info sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={160}>
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                          <XAxis dataKey="time" tick={{ fontSize: 9 }} interval={xIntervalSub} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <ReTooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v?.toFixed(2)}%`, 'Bandbreite']} />
                          <Area type="monotone" dataKey="bb_width" stroke="#E91E63" fill="rgba(233,30,99,0.1)" strokeWidth={1.5} dot={false} name="BB Width" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* ATR */}
              {subChartsToShow.includes('atr') && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>Volatilität (High-Low Range)</Typography>
                        <Tooltip title="Zeigt die Schwankungsbreite pro Kerze. Breakout nutzt ATR für Stop-Loss-Berechnung (ATR × 2)." arrow>
                          <Info sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={160}>
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                          <XAxis dataKey="time" tick={{ fontSize: 9 }} interval={xIntervalSub} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <ReTooltip contentStyle={tooltipStyle} formatter={(v: number) => [`€${v?.toFixed(2)}`, 'Range']} />
                          <Bar dataKey="high" fill="rgba(76,175,80,0.15)" name="High" />
                          <Bar dataKey="low" fill="rgba(244,67,54,0.15)" name="Low" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* ═══ Trade-Historie dieser Strategie ═══ */}
            {trades.length > 0 && (
              <Card sx={{ borderRadius: 2, mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Bot-Trades dieser Strategie ({trades.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {trades.slice().reverse().map((t) => (
                      <Card
                        key={t.id}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          borderLeft: 4,
                          borderColor: t.side === 'buy' ? 'success.main' : 'error.main',
                        }}
                      >
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={t.side === 'buy' ? 'KAUF' : 'VERKAUF'}
                                size="small"
                                color={t.side === 'buy' ? 'success' : 'error'}
                                sx={{ fontWeight: 700, minWidth: 80 }}
                              />
                              <Typography variant="body2" fontWeight={600}>
                                {t.amount.toFixed(6)} @ €{t.price.toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                = €{t.total.toFixed(2)} (Gebühr: €{t.fee.toFixed(2)})
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(t.timestamp).toLocaleString('de-DE')}
                            </Typography>
                          </Box>
                          {t.reason && (
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                              <Typography variant="caption" fontWeight={600} color="text.secondary">
                                Warum wurde gehandelt?
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, fontSize: '0.8rem' }}>
                                {t.reason}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* ═══ Detected Patterns ═══ */}
            {data.patterns.length > 0 && (() => {
              const bullish = data.patterns.filter(p => p.signal === 'bullish').length;
              const bearish = data.patterns.filter(p => p.signal === 'bearish').length;
              const neutral = data.patterns.filter(p => p.signal === 'neutral').length;
              const highPriority = data.patterns.filter(p => p.severity === 'high').length;
              const overall = bullish > bearish ? 'bullish' : bearish > bullish ? 'bearish' : 'neutral';
              const overallLabel = overall === 'bullish' ? 'Bullisch' : overall === 'bearish' ? 'Bärisch' : 'Neutral';
              const overallColor = overall === 'bullish' ? 'success' : overall === 'bearish' ? 'error' : 'info';
              return (
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    <Warning sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                    Erkannte Muster & Signale ({data.patterns.length})
                  </Typography>

                  {/* Zusammenfassung */}
                  <Alert severity={overallColor as any} sx={{ mb: 2, borderRadius: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Gesamtbild: {overallLabel}
                    </Typography>
                    <Typography variant="body2">
                      {bullish} bullische, {bearish} bärische, {neutral} neutrale Signale erkannt
                      {highPriority > 0 && ` — davon ${highPriority} mit hoher Priorität`}.
                      {overall === 'bullish' && ' Die Mehrheit der Indikatoren deutet auf steigende Kurse hin.'}
                      {overall === 'bearish' && ' Die Mehrheit der Indikatoren deutet auf fallende Kurse hin.'}
                      {overall === 'neutral' && ' Die Signale sind gemischt — Vorsicht und Abwarten empfohlen.'}
                    </Typography>
                  </Alert>

                  {/* Strategie-Empfehlung hervorheben */}
                  {data.patterns.find(p => p.type === 'strategy_recommendation') && (() => {
                    const rec = data.patterns.find(p => p.type === 'strategy_recommendation')!;
                    return (
                      <Paper
                        elevation={3}
                        sx={{
                          mb: 2, p: 2, borderRadius: 2,
                          border: 2,
                          borderColor: rec.signal === 'bullish' ? 'success.main' : rec.signal === 'bearish' ? 'error.main' : 'info.main',
                          bgcolor: rec.signal === 'bullish' ? 'rgba(76,175,80,0.06)' : rec.signal === 'bearish' ? 'rgba(239,83,80,0.06)' : 'rgba(33,150,243,0.06)',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          {signalIcon(rec.signal)}
                          <Typography variant="subtitle1" fontWeight={700}>{rec.name}</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 1 }}>{rec.description}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{rec.explanation}</Typography>
                        <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2" fontWeight={600}>Empfohlene Aktion:</Typography>
                          <Typography variant="body2">{rec.action}</Typography>
                        </Box>
                      </Paper>
                    );
                  })()}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {data.patterns.filter(p => p.type !== 'strategy_recommendation').map((p) => {
                      const isExpanded = expandedPattern === p.type;
                      return (
                        <Card
                          key={p.type}
                          variant="outlined"
                          sx={{
                            borderRadius: 2, cursor: 'pointer',
                            borderLeft: 4,
                            borderColor: p.signal === 'bullish' ? 'success.main' : p.signal === 'bearish' ? 'error.main' : 'text.disabled',
                          }}
                          onClick={() => setExpandedPattern(isExpanded ? null : p.type)}
                        >
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {signalIcon(p.signal)}
                                <Typography variant="subtitle2" fontWeight={600}>{p.name}</Typography>
                                <Chip
                                  label={p.signal === 'bullish' ? 'Bullisch' : p.signal === 'bearish' ? 'Bärisch' : 'Neutral'}
                                  size="small"
                                  color={p.signal === 'bullish' ? 'success' : p.signal === 'bearish' ? 'error' : 'default'}
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                                <Chip label={p.severity === 'high' ? 'Wichtig' : p.severity === 'medium' ? 'Beachten' : 'Info'} size="small" color={severityColor(p.severity) as any} variant="outlined" />
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                {isExpanded ? 'Weniger ▲' : 'Mehr ▼'}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {p.description}
                            </Typography>
                            {isExpanded && (
                              <Box sx={{ mt: 1.5 }}>
                                <Divider sx={{ mb: 1.5 }} />
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  Was bedeutet das?
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-line' }}>
                                  {p.explanation}
                                </Typography>
                                <Box sx={{
                                  p: 1.5, borderRadius: 1.5,
                                  bgcolor: p.signal === 'bullish' ? 'rgba(76,175,80,0.08)' : p.signal === 'bearish' ? 'rgba(239,83,80,0.08)' : 'action.hover',
                                }}>
                                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    {p.signal === 'bullish' ? '💡 Handlungsempfehlung (Kauf):' : p.signal === 'bearish' ? '⚠️ Handlungsempfehlung (Vorsicht):' : '📋 Handlungsempfehlung:'}
                                  </Typography>
                                  <Typography variant="body2">{p.action}</Typography>
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
              );
            })()}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StrategyAnalysis;
